import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import NavTitle from '../../../components/nav-title'
import TextNavTitle from '../../../components/TextNavTitle'
import { useRightOrgProfileStore } from '../../../store/layout'
import { useSelector, useDispatch } from 'react-redux'
import { Controller, useForm } from 'react-hook-form'
import ExportExcel from './ExportExcel'
import SelectMutiCustom from './SelectMutiCustom'
import { fnDateEn } from '../../../helper/fnDateEn'
import { getStatusAttendanceColor } from '../../../helper/getStatusAttendanceColor'
import UseEmpsTypes from '../../../hooks/use-emps-types'
import UseEmpsStatus from '../../../hooks/use-emps-status'
import ImgUser from '../../../images/user.png'
import useReportAttendanceSummary from '../../../hooks/report/attendance/use-report-attendance-summary'
import UseReportAttendanceDropStaff from '../../../hooks/report/attendance/use-report-drop-staff'
import useExportReportAttendanceSummary from '../../../hooks/report/attendance/use-export-report-attendance-summary'
import UseEmpsProject from '../../../hooks/use-emps-project'
import UseEmpsLocation from '../../../hooks/use-emps-airport'
import UseEmpsReportby from '../../../hooks/use-emps-reportby'
import UseReportDropGroups from '../../../hooks/dropdown/use-report-drop-groups'
import UseReportDropDivisions from '../../../hooks/dropdown/use-report-drop-divisions'
import UseReportDropDepartments from '../../../hooks/dropdown/use-report-drop-departments'
import UseReportDropTeams from '../../../hooks/dropdown/use-report-drop-teams'
import UseReportAttendanceDropPositions from '../../../hooks/report/attendance/use-report-drop-positions'
import UseReportAttendanceDropSummaryStatus from '../../../hooks/report/attendance/use-report-drop-status'
import UseReportAttendanceDropReportTo from '../../../hooks/report/attendance/use-report-drop-report-to'
import { updateRole } from '../../../actions/role-org.action'
import { t } from 'i18next'
import AlertSuccess from '../../../alert/AlertSuccess'

import {
  Table,
  Card,
  Select,
  DatePicker,
  Button,
  Tag,
  Space,
  Avatar,
  Typography,
  Row,
  Col,
  Form,
  ConfigProvider,
  theme,
  Tooltip,
  Pagination,
  AutoComplete,
  Drawer,
  Grid,
  Empty,
  Input
} from 'antd'

import { SearchOutlined } from '@ant-design/icons'
import {
  LuChevronUp,
  LuChevronDown,
  LuClock,
  LuMapPin,
  LuCalendarMinus2,
  LuFilter
} from 'react-icons/lu'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(customParseFormat)

const { Text } = Typography

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const PAGE_SIZE_SELECT_OPTIONS = PAGE_SIZE_OPTIONS.map((n) => ({
  value: n,
  label: String(n)
}))

const SELECT_SUFFIX_ICON = (
  <LuChevronDown
    size={16}
    style={{ color: 'var(--color-on-base-content-high)' }}
  />
)

const getCheckInColor = (status) => {
  const shouldHighlight = status === 'LATE' || status === 'LATE_EARLY'
  return shouldHighlight
    ? 'var(--color-functional-content-error)'
    : 'var(--color-on-base-content-subtle)'
}

const getCheckOutColor = (status) => {
  const shouldHighlight = status === 'ONTIME_EARLY' || status === 'LATE_EARLY'
  return shouldHighlight
    ? 'var(--color-functional-content-error)'
    : 'var(--color-on-base-content-subtle)'
}

const ColumnTitle = ({ children }) => (
  <Text
    style={{
      fontWeight: 500,
      fontSize: 12,
      color: 'var(--color-on-base-content-high)'
    }}
    ellipsis={{ tooltip: true }}
  >
    {children}
  </Text>
)

const EllipsisTooltip = ({ children, title, style, onClick }) => {
  const textRef = useRef(null)
  const [isEllipsed, setIsEllipsed] = useState(false)

  const checkEllipsis = useCallback(() => {
    if (textRef.current) {
      setIsEllipsed(textRef.current.scrollWidth > textRef.current.clientWidth)
    }
  }, [])

  useEffect(() => {
    checkEllipsis()
    window.addEventListener('resize', checkEllipsis, { passive: true })
    return () => window.removeEventListener('resize', checkEllipsis)
  }, [checkEllipsis, title])

  const textElement = (
    <Text
      ref={textRef}
      onClick={onClick}
      style={{
        ...style,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      {children}
    </Text>
  )

  if (isEllipsed) {
    return <Tooltip title={title}>{textElement}</Tooltip>
  }

  return textElement
}

const StatusTag = ({ status }) => {
  if (!status) return '-'
  const colors = getStatusAttendanceColor(status)

  return (
    <Tag
      className="text-center"
      style={{
        background: colors.bg,
        color: colors.text,
        border: 0,
        minWidth: '90px',
        fontWeight: 500,
        padding: '4px 8px',
        fontSize: 12,
        borderRadius: '6px'
      }}
    >
      {t(status)}
    </Tag>
  )
}

const CheckCell = ({ time, location, locationFull, color, lat, lng }) => {
  const handleLocationClick = () => {
    if (lat && lng) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
        '_blank'
      )
    }
  }

  const hasCoords = lat && lng
  const locationColor = hasCoords
    ? 'var(--color-functional-content-info)'
    : 'var(--color-on-base-content-subtle)'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <LuClock
          size={14}
          style={{ color: 'var(--color-on-base-content-subtle)', minWidth: 14 }}
        />
        <Text
          style={{
            fontSize: 12,
            fontWeight: 400,
            color: color || 'var(--color-on-base-content-subtle)'
          }}
        >
          {time || '--:--'}
        </Text>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <LuMapPin
          size={14}
          style={{
            color: 'var(--color-on-base-content-subtle)',
            minWidth: 14
          }}
        />
        <EllipsisTooltip
          title={locationFull || location}
          onClick={handleLocationClick}
          style={{
            fontSize: 12,
            fontWeight: 400,
            color: locationColor,
            maxWidth: 150,
            cursor: hasCoords ? 'pointer' : 'default'
          }}
        >
          {location || '-'}
        </EllipsisTooltip>
      </div>
    </div>
  )
}

const EmployeeNameCell = ({ fullName, employeeId, avatar }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <Avatar
      size={40}
      src={avatar || ImgUser}
      style={{ width: 30, height: 30 }}
    />
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <EllipsisTooltip
        title={fullName}
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: 'var(--color-on-base-content-subtle)',
          maxWidth: 200
        }}
      >
        {fullName}
      </EllipsisTooltip>
      <Text
        style={{
          fontSize: 9,
          fontWeight: 400,
          color: 'var(--color-on-base-content-subtle)'
        }}
      >
        {employeeId}
      </Text>
    </div>
  </div>
)

export default function ReportAttendancePage() {
  const roleId = useSelector((state) => state.roleReducer.role)
  const dispatch = useDispatch()
  const {
    data: dataTable,
    total: totalRow,
    isLoading,
    fetchAttendanceSummary
  } = useReportAttendanceSummary()
  const { exportAttendanceSummary, isExporting } =
    useExportReportAttendanceSummary()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [showMoreFilter, setShowMoreFilter] = useState(false)
  const [defaultStartDate] = useState(() => dayjs().startOf('day').toDate())
  const [defaultEndDate] = useState(() => dayjs().endOf('day').toDate())

  const screens = Grid.useBreakpoint()
  const [showMobileDrawer, setShowMobileDrawer] = useState(false)

  const [selectedDateRange, setSelectedDateRange] = useState(() => [
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ])

  const { orgRole } = useRightOrgProfileStore()

  const orgOptions = orgRole.map((d) => ({ label: d.org, value: d.org_id }))

  const { control, setValue, handleSubmit, reset, getValues, watch } = useForm({
    defaultValues: {
      search: '',
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      org: null,
      project: null,
      location: null,
      group: null,
      division: null,
      department: null,
      team: null,
      position: null,
      typeId: null,
      supervisor: null,
      reportTo: null,
      employeeStatus: null,
      status: []
    }
  })

  const selectedOrg = watch('org')
  const orgIdForDrops = selectedOrg ?? roleId
  const selectedGroup = watch('group')
  const selectedDivision = watch('division')
  const selectedDepartment = watch('department')
  const selectedTeam = watch('team')
  const hasAnyOrgSelection = !!(
    selectedGroup ||
    selectedDivision ||
    selectedDepartment ||
    selectedTeam
  )
  // eslint-disable-next-line no-unused-vars
  const {
    options: groupOptions,
    rawData: groupRawData,
    loading: groupLoading
  } = UseReportDropGroups(orgIdForDrops)
  const {
    options: divisionOptions,
    rawData: divisionRawData,
    loading: divisionLoading
  } = UseReportDropDivisions(orgIdForDrops, selectedGroup)
  const {
    options: departmentOptions,
    rawData: departmentRawData,
    loading: departmentLoading
  } = UseReportDropDepartments(orgIdForDrops, selectedGroup, selectedDivision)
  const {
    options: teamOptions,
    rawData: teamRawData,
    loading: teamLoading
  } = UseReportDropTeams(orgIdForDrops, selectedGroup, selectedDepartment)
  const { options: positionOptions } = UseReportAttendanceDropPositions(
    orgIdForDrops,
    selectedGroup,
    selectedDepartment,
    selectedTeam
  )
  const { options: summaryStatusOptions } =
    UseReportAttendanceDropSummaryStatus()
  const typeOptions = UseEmpsTypes()
  const employeeStatusOptions = UseEmpsStatus()

  const { token } = theme.useToken()
  const projectOptions = UseEmpsProject(selectedOrg)
  const locationOptions = UseEmpsLocation()

  const [reportToSearch, setReportToSearch] = useState('')
  const [reportToSearchDebounce, setReportToSearchDebounce] = useState('')

  const { options: headOptions } = UseReportAttendanceDropReportTo({
    orgId: orgIdForDrops,
    search: reportToSearchDebounce,
    groupId: selectedGroup,
    dsId: selectedDivision,
    dmId: selectedDepartment,
    teamId: selectedTeam
  })

  const [staffSearch, setStaffSearch] = useState('')
  const staffDebounceRef = useRef(null)
  const { options: staffOptions } = UseReportAttendanceDropStaff(
    orgIdForDrops,
    staffSearch
  )

  const reportToDebounceRef = useRef(null)

  const handleReportToSearch = useCallback((val) => {
    setReportToSearch(val)
    if (reportToDebounceRef.current) clearTimeout(reportToDebounceRef.current)
    reportToDebounceRef.current = setTimeout(() => {
      setReportToSearchDebounce(val)
    }, 550)
  }, [])

  const handleStaffSearch = useCallback((val) => {
    if (staffDebounceRef.current) clearTimeout(staffDebounceRef.current)
    staffDebounceRef.current = setTimeout(() => {
      setStaffSearch(val)
    }, 550)
  }, [])

  const buildFilterBody = (formData, overrides = {}) => {
    // console.log('formData:', formData)
    const payload = {
      page: 1,
      limit,
      search:
        staffOptions.find((d) => d.label === formData.search)?.value ||
        formData.search ||
        '',
      startDate: fnDateEn(formData.startDate).format('YYYY-MM-DD'),
      endDate: fnDateEn(formData.endDate).format('YYYY-MM-DD'),
      orgId: formData.org ?? roleId,
      projectId: formData.project ?? '',
      locationId: formData.location ?? '',
      groupId: formData.group ?? '',
      dsId: formData.division ?? '',
      dmId: formData.department ?? '',
      teamId: formData.team ?? '',
      position: formData.position ?? '',
      empTypeId: formData.typeId ?? '',
      supervisor: formData.supervisor ?? '',
      reportTo:
        headOptions.find((d) => d.label === formData.reportTo)?.value ||
        formData.reportTo ||
        '',
      empStatusId: formData.employeeStatus ?? '',
      status:
        Array.isArray(formData.status) && formData.status.length > 0
          ? formData.status
          : '',
      ...overrides
    }
    // console.log('payload:', payload)
    return payload
  }

  const handleGroupChange = (val) => {
    setValue('group', val ?? null)
    setValue('division', null)
    setValue('department', null)
    setValue('team', null)
  }

  const handleDivisionChange = (val) => {
    setValue('division', val ?? null)
    setValue('department', null)
    setValue('team', null)
    if (val) {
      const raw = divisionRawData.find((d) => d.dsId === val)
      if (raw?.groupId) setValue('group', raw.groupId)
    }
  }

  const handleDepartmentChange = (val) => {
    setValue('department', val ?? null)
    setValue('team', null)
    if (val) {
      const raw = departmentRawData.find((d) => d.dmId === val)
      if (raw?.dsId) setValue('division', raw.dsId)
      if (raw?.groupId) setValue('group', raw.groupId)
    }
  }

  const handleTeamChange = (val) => {
    setValue('team', val ?? null)
    if (val) {
      const raw = teamRawData.find((d) => d.teamId === val)
      if (raw?.dmId) setValue('department', raw.dmId)
      if (raw?.dsId) setValue('division', raw.dsId)
      if (raw?.groupId) setValue('group', raw.groupId)
    }
  }

  useEffect(() => {
    setValue('project', null)
    setValue('reportTo', null)
    setValue('group', null)
    setValue('division', null)
    setValue('department', null)
    setValue('team', null)
    setValue('position', null)
  }, [selectedOrg, setValue])

  useEffect(() => {
    if (roleId && orgRole?.length) {
      const currentOrg = orgRole.find(
        (d) => String(d.org_id) === String(roleId)
      )
      if (currentOrg) {
        setValue('org', currentOrg.org_id)
      }
    }
  }, [roleId, orgRole, setValue])

  useEffect(() => {
    if (roleId) {
      const d = getValues()
      const body = buildFilterBody(d, {
        page: 1,
        limit,
        orgId: roleId
      })
      setPage(1)
      fetchAttendanceSummary(body)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId])

  const onApplyFilter = (d) => {
    if (d.org && String(d.org) !== String(roleId)) {
      dispatch(updateRole(d.org))
      return
    }
    const body = buildFilterBody(d)
    setPage(1)
    fetchAttendanceSummary(body)
  }

  const onClearFilter = () => {
    const clearDay = new Date()
    const clearStart = dayjs(clearDay).startOf('day').toDate()
    const clearEnd = dayjs(clearDay).endOf('day').toDate()
    setStaffSearch('')
    if (staffDebounceRef.current) clearTimeout(staffDebounceRef.current)
    reset({
      search: '',
      startDate: clearStart,
      endDate: clearEnd,
      org: orgIdForDrops,
      project: null,
      location: null,
      group: null,
      division: null,
      department: null,
      team: null,
      position: null,
      typeId: null,
      supervisor: null,
      reportTo: null,
      employeeStatus: null,
      status: []
    })
    setSelectedDateRange([dayjs(clearStart), dayjs(clearEnd)])
    const body = {
      page: 1,
      limit,
      orgId: roleId,
      startDate: fnDateEn(clearStart).format('YYYY-MM-DD'),
      endDate: fnDateEn(clearEnd).format('YYYY-MM-DD')
    }
    setPage(1)
    fetchAttendanceSummary(body)
  }

  const columns = useMemo(
    () => [
      {
        title: <ColumnTitle>{t('hrms_attendance_no_table')}</ColumnTitle>,
        dataIndex: 'no',
        key: 'no',
        width: 50,
        align: 'center',
        fixed: 'left',
        render: (_, __, index) => (
          <Text
            style={{
              fontSize: 12,
              color: 'var(--color-on-base-content-subtle)'
            }}
          >
            {(page - 1) * limit + index + 1}
          </Text>
        )
      },
      {
        title: <ColumnTitle>{t('hrms_attendance_date_table')}</ColumnTitle>,
        dataIndex: 'attendanceWorkDate',
        key: 'attendanceWorkDate',
        width: 100,
        align: 'center',
        fixed: 'left',
        render: (date) => (
          <Text
            style={{
              fontSize: 12,
              color: 'var(--color-on-base-content-subtle)'
            }}
          >
            {date ? dayjs(date).format('DD/MM/YYYY') : '-'}
          </Text>
        )
      },
      {
        title: <ColumnTitle>{t('hrms_attendance_name_table')}</ColumnTitle>,
        dataIndex: 'fullname',
        key: 'fullname',
        width: 200,
        render: (_, record) => (
          <EmployeeNameCell
            fullName={record.fullname}
            employeeId={record.code}
            avatar={record.avatar}
          />
        )
      },
      {
        title: <ColumnTitle>{t('hrms_attendance_org_table')}</ColumnTitle>,
        dataIndex: 'org',
        key: 'org',
        width: 100,
        align: 'center',
        render: (org) => (
          <Text
            style={{
              fontSize: 12,
              color: 'var(--color-on-base-content-subtle)'
            }}
          >
            {org || '-'}
          </Text>
        )
      },
      {
        title: <ColumnTitle>{t('hrms_attendance_group_table')}</ColumnTitle>,
        dataIndex: 'group',
        key: 'group',
        width: 80,
        align: 'center',
        render: (group, record) => (
          <Text
            style={{
              fontSize: 12,
              color: 'var(--color-on-base-content-subtle)'
            }}
          >
            <Tooltip title={record.groupName}>{group || '-'}</Tooltip>
          </Text>
        )
      },
      {
        title: (
          <ColumnTitle>{t('hrms_attendance_department_table')}</ColumnTitle>
        ),
        dataIndex: 'department',
        key: 'department',
        width: 100,
        align: 'center',
        render: (department, record) => (
          <Text
            style={{
              fontSize: 12,
              color: 'var(--color-on-base-content-subtle)'
            }}
          >
            <Tooltip title={record.departmentName}>{department || '-'}</Tooltip>
          </Text>
        )
      },
      {
        title: (
          <ColumnTitle>{t('hrms_attendance_working_hrs_table')}</ColumnTitle>
        ),
        dataIndex: 'workingHrs',
        key: 'workingHrs',
        width: 100,
        align: 'center',
        render: (workingHrs, record) => {
          const formatDateTime = (dateStr) => {
            if (!dateStr) return '-'
            const date = new Date(dateStr)
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            return `${day}/${month}/${year} ${hours}:${minutes}`
          }

          const checkin = formatDateTime(record?.checkIn)
          const checkout = formatDateTime(record?.checkOut)

          const tooltipTitle =
            record?.checkIn || record?.checkOut
              ? `${checkin} - ${checkout}`
              : null

          return (
            <Tooltip title={tooltipTitle}>
              <Text
                style={{
                  fontSize: 12,
                  color: 'var(--color-on-base-content-subtle)'
                }}
              >
                {workingHrs || '-'}
              </Text>
            </Tooltip>
          )
        }
      },
      {
        title: <ColumnTitle>{t('hrms_attendance_status_table')}</ColumnTitle>,
        dataIndex: 'status',
        key: 'status',
        width: 120,
        align: 'center',
        render: (status) => <StatusTag status={status} />
      },
      {
        title: <ColumnTitle>{t('hrms_attendance_check_in_table')}</ColumnTitle>,
        dataIndex: 'checkin',
        key: 'checkin',
        width: 200,
        render: (_, record) => (
          <CheckCell
            time={record.checkIn ? dayjs(record.checkIn).format('HH:mm') : null}
            location={record.locationCheckIn}
            locationFull={record.locationCheckIn}
            color={getCheckInColor(record.status)}
            lat={record.latIn}
            lng={record.lonIn}
          />
        )
      },
      {
        title: (
          <ColumnTitle>{t('hrms_attendance_check_out_table')}</ColumnTitle>
        ),
        dataIndex: 'checkout',
        key: 'checkout',
        width: 200,
        render: (_, record) => (
          <CheckCell
            time={
              record.checkOut ? dayjs(record.checkOut).format('HH:mm') : null
            }
            location={record.locationCheckOut}
            locationFull={record.locationCheckOut}
            color={getCheckOutColor(record.status)}
            lat={record.latOut}
            lng={record.lonOut}
          />
        )
      }
    ],
    [page, limit, t]
  )

  const handleTableChange = (pagination) => {
    const d = getValues()
    const body = buildFilterBody(d, {
      page: pagination.current,
      limit: pagination.pageSize,
      orgId: roleId
    })
    setLimit(pagination.pageSize)
    fetchAttendanceSummary(body)
    setPage(pagination.current)
  }

  const handleDateChange = (dates) => {
    if (!dates) return
    setSelectedDateRange(dates)
    setValue('startDate', dates[0].toDate())
    setValue('endDate', dates[1].toDate())
  }

  const handlePaginationChange = (newPage, newPageSize) => {
    const d = getValues()
    const body = buildFilterBody(d, { page: newPage, limit: newPageSize })
    setPage(newPage)
    setLimit(newPageSize)
    fetchAttendanceSummary(body)
  }

  const renderFilterSelect = ({
    name,
    placeholder,
    options,
    span,
    disabled,
    onChange: customOnChange,
    showSearch: customShowSearch,
    onSearch: customOnSearch,
    isAutoComplete
  }) => (
    <Col xs={24} md={span} key={name}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          if (isAutoComplete) {
            return (
              <AutoComplete
                {...field}
                value={field.value || ''}
                options={options}
                onSearch={customOnSearch}
                onSelect={(val, option) => {
                  field.onChange(option.label)
                  if (customOnChange) customOnChange(option.label)
                }}
                onChange={(val) => {
                  if (customOnChange) {
                    customOnChange(val)
                  } else {
                    field.onChange(val)
                  }
                }}
                onClear={() => {
                  if (customOnChange) {
                    customOnChange('')
                  } else {
                    field.onChange('')
                  }
                }}
                allowClear
                style={{ width: '100%' }}
                placeholder={placeholder}
                disabled={disabled}
                suffixIcon={SELECT_SUFFIX_ICON}
                notFoundContent={
                  field.value ? (
                    <div style={{ textAlign: 'center', padding: 8 }}>
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={'No data'}
                      />
                    </div>
                  ) : null
                }
              />
            )
          }

          return (
            <Select
              {...field}
              allowClear
              showSearch={customShowSearch ?? true}
              placeholder={placeholder}
              options={options}
              style={{ width: '100%' }}
              suffixIcon={SELECT_SUFFIX_ICON}
              disabled={disabled}
              filterOption={(input, option) =>
                customOnSearch
                  ? false
                  : (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
              }
              onSearch={customOnSearch}
              onChange={(val) => {
                if (customOnChange) {
                  customOnChange(val)
                } else {
                  field.onChange(val)
                }
              }}
            />
          )
        }}
      />
    </Col>
  )

  const moreFiltersConfig = [
    {
      name: 'project',
      placeholder: t('hrms_attendance_search_project'),
      options: projectOptions,
      span: 5,
      disabled: !selectedOrg
    },
    {
      name: 'group',
      placeholder: t('hrms_attendance_search_group'),
      options: groupOptions,
      span: 5,
      onChange: handleGroupChange,
      disabled: groupLoading
    },
    {
      name: 'division',
      placeholder: t('hrms_attendance_search_division'),
      options: divisionOptions,
      span: 5,
      onChange: handleDivisionChange,
      disabled: divisionLoading || (hasAnyOrgSelection && !selectedGroup)
    },
    {
      name: 'department',
      placeholder: t('hrms_attendance_search_department'),
      options: departmentOptions,
      span: 5,
      onChange: handleDepartmentChange,
      disabled: departmentLoading || (hasAnyOrgSelection && !selectedDivision)
    },
    {
      name: 'team',
      placeholder: t('hrms_attendance_search_team'),
      options: teamOptions,
      span: 4,
      onChange: handleTeamChange,
      disabled: teamLoading || (hasAnyOrgSelection && !selectedDepartment)
    },
    {
      name: 'position',
      placeholder: t('hrms_attendance_search_position'),
      options: positionOptions,
      span: 5
    },
    {
      name: 'reportTo',
      placeholder: t('hrms_attendance_search_report_to'),
      options: headOptions,
      span: 5,
      disabled: !selectedOrg,
      showSearch: true,
      isAutoComplete: true,
      onSearch: (val) => {
        handleReportToSearch(val)
      },
      onChange: (val) => {
        if (!val) {
          handleReportToSearch('')
          setValue('reportTo', '')
        } else {
          setValue('reportTo', val)
        }
      }
    },
    {
      name: 'typeId',
      placeholder: t('hrms_attendance_search_employee'),
      options: typeOptions,
      span: 5
    },
    {
      name: 'employeeStatus',
      placeholder: t('hrms_attendance_search_employee_status'),
      options: employeeStatusOptions,
      span: 5
    }
  ]

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            headerFontSize: 16
          }
        }
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 20px)'
        }}
      >
        <NavTitle>
          <TextNavTitle>{t('hrms_attendance_topic')}</TextNavTitle>
        </NavTitle>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            gap: 16
          }}
        >
          <div>
            <div className="d-flex justify-content-between align-items-center">
              <Text
                style={{
                  color: 'var(--color-on-base-content-high)',
                  fontWeight: 500,
                  fontSize: 14
                }}
              >
                {t('hrms_attendance_filter')}
              </Text>

              {screens.md && (
                <Button
                  type="link"
                  onClick={() => setShowMoreFilter((prev) => !prev)}
                  iconPosition="right"
                  style={{
                    color: 'var(--color-primary-content-default)',
                    fontWeight: 500,
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  {showMoreFilter
                    ? t('hrms_attendance_less_filter')
                    : t('hrms_attendance_more_filter')}
                  {showMoreFilter ? (
                    <LuChevronUp size={16} />
                  ) : (
                    <LuChevronDown size={16} />
                  )}
                </Button>
              )}
            </div>
            <Form layout="vertical" onFinish={handleSubmit(onApplyFilter)}>
              <Row gutter={[12, 12]}>
                <Col xs={21} md={10}>
                  <Controller
                    name="search"
                    control={control}
                    render={({ field }) => (
                      <AutoComplete
                        value={field.value}
                        options={staffOptions}
                        onSearch={(val) => {
                          field.onChange(val)
                          if (!val) {
                            if (staffDebounceRef.current)
                              clearTimeout(staffDebounceRef.current)
                            setStaffSearch('')
                            return
                          }
                          handleStaffSearch(val)
                        }}
                        onSelect={(val, option) => {
                          field.onChange(option.label)
                        }}
                        onClear={() => {
                          field.onChange('')
                          if (staffDebounceRef.current)
                            clearTimeout(staffDebounceRef.current)
                          setStaffSearch('')
                        }}
                        allowClear
                        style={{ width: '100%' }}
                        prefix={<SearchOutlined />}
                        placeholder={t('hrms_attendance_search_name')}
                        enterKeyHint="search"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleSubmit(onApplyFilter)()
                          }
                        }}
                        notFoundContent={
                          field.value ? (
                            <div style={{ textAlign: 'center', padding: 8 }}>
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={'No data'}
                              />
                            </div>
                          ) : null
                        }
                      ></AutoComplete>
                    )}
                  />
                </Col>

                {!screens.md && (
                  <Col xs={3}>
                    <Button
                      icon={<LuFilter size={16} />}
                      onClick={() => setShowMobileDrawer(true)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                )}

                {screens.md && (
                  <>
                    <Col xs={24} md={5}>
                      <DatePicker.RangePicker
                        value={selectedDateRange}
                        onChange={handleDateChange}
                        suffixIcon={
                          <LuCalendarMinus2
                            style={{
                              color: 'var(--color-on-base-content-high)'
                            }}
                          />
                        }
                        format="DD/MM/YYYY"
                        allowClear={false}
                        style={{ width: '100%' }}
                      />
                    </Col>

                    <Col xs={24} md={5}>
                      <Controller
                        name="org"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Org"
                            options={orgOptions}
                            style={{ width: '100%' }}
                            suffixIcon={SELECT_SUFFIX_ICON}
                            onChange={(val) => {
                              field.onChange(val)
                            }}
                            showSearch
                            filterOption={(input, option) => {
                              return option.label
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }}
                          />
                        )}
                      />
                    </Col>

                    {renderFilterSelect({
                      name: 'location',
                      placeholder: t('hrms_attendance_search_location'),
                      options: locationOptions,
                      span: 4
                    })}
                  </>
                )}
              </Row>

              {screens.md && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateRows: showMoreFilter ? 'minmax(0, 1fr)' : '0fr',
                    opacity: showMoreFilter ? 1 : 0,
                    marginTop: showMoreFilter ? '12px' : '0',
                    transition:
                      'grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-top 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <div style={{ overflow: 'hidden' }}>
                    <Row gutter={[12, 12]}>
                      {moreFiltersConfig.map(renderFilterSelect)}
                      <Col xs={24} md={4}>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <SelectMutiCustom
                              options={summaryStatusOptions}
                              value={field.value || []}
                              onChange={(val) => field.onChange(val)}
                              placeholder={t('hrms_attendance_search_status')}
                              isStatus
                              hasSelectAll={false}
                            />
                          )}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              )}

              <Drawer
                title={t('hrms_attendance_filter')}
                open={showMobileDrawer}
                onClose={() => setShowMobileDrawer(false)}
                width={'100%'}
                styles={{
                  body: {
                    paddingBottom: 80
                  }
                }}
              >
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                  <Col xs={24}>
                    <DatePicker.RangePicker
                      value={selectedDateRange}
                      onChange={handleDateChange}
                      suffixIcon={
                        <LuCalendarMinus2
                          style={{
                            color: 'var(--color-on-base-content-high)'
                          }}
                        />
                      }
                      format="DD/MM/YYYY"
                      allowClear={false}
                      style={{ width: '100%' }}
                    />
                  </Col>

                  <Col xs={24}>
                    <Controller
                      name="org"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Org"
                          options={orgOptions}
                          style={{ width: '100%' }}
                          suffixIcon={SELECT_SUFFIX_ICON}
                          onChange={(val) => {
                            field.onChange(val)
                          }}
                        />
                      )}
                    />
                  </Col>
                  {renderFilterSelect(
                    {
                      name: 'location',
                      placeholder: t('hrms_attendance_search_location'),
                      options: locationOptions,
                      span: 24
                    },
                    24
                  )}
                  {moreFiltersConfig.map((item) =>
                    renderFilterSelect({ ...item, span: 24 }, 24)
                  )}
                  <Col xs={24}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <SelectMutiCustom
                          options={summaryStatusOptions}
                          value={field.value || []}
                          onChange={(val) => field.onChange(val)}
                          placeholder={t('hrms_attendance_search_status')}
                          isStatus
                          hasSelectAll={false}
                        />
                      )}
                    />
                  </Col>
                </div>

                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8
                  }}
                >
                  <Button onClick={onClearFilter} style={{ flex: 1 }}>
                    {t('hrms_attendance_clear_bt')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      handleSubmit(onApplyFilter)()
                      setShowMobileDrawer(false)
                    }}
                    style={{
                      flex: 1,
                      background:
                        'var(--comp-btn-color-background-primary-normal)'
                    }}
                  >
                    {t('hrms_attendance_apply_bt')}
                  </Button>
                </div>
              </Drawer>

              {screens.md && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginTop: '16px',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}
                >
                  <div>
                    <Space>
                      <Button
                        onClick={onClearFilter}
                        style={{
                          background: 'none',
                          border: 'none',
                          boxShadow: 'none',
                          fontWeight: 500,
                          fontSize: 12
                        }}
                      >
                        {t('hrms_attendance_clear_bt')}
                      </Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        disabled={isLoading || isExporting}
                        style={{
                          background:
                            'var(--comp-btn-color-background-primary-normal)',
                          border: 'none',
                          boxShadow: 'none',
                          color: 'var(--color-on-primary-content-high)',
                          fontWeight: 500,
                          fontSize: 12
                        }}
                      >
                        {t('hrms_attendance_apply_bt')}
                      </Button>
                    </Space>
                  </div>
                </div>
              )}
            </Form>
          </div>

          <Card
            variant="borderless"
            style={{
              overflow: 'hidden',
              minWidth: '100%',
              padding: 0,
              flex: 1,
              minHeight: 0
            }}
            styles={{
              body: {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }
            }}
          >
            <div
              style={{
                padding: '16px 24px',
                borderBottom: `1px solid ${token.colorSplit}`
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <Text style={{ fontWeight: 500, fontSize: 14 }}>
                    {t('hrms_attendance_total')}:{' '}
                    <Text style={{ fontWeight: 500, fontSize: 14 }}>
                      {totalRow?.toLocaleString()}
                    </Text>
                  </Text>
                </Col>
                <Col>
                  <ExportExcel
                    loading={isExporting}
                    disabled={isLoading || isExporting}
                    fnExportExcel={async () => {
                      const d = getValues()
                      const success = await exportAttendanceSummary(
                        buildFilterBody(d)
                      )
                      if (success) {
                        AlertSuccess(t('hrms_attendance_download_file_success'))
                      }
                    }}
                  />
                </Col>
              </Row>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <ConfigProvider
                theme={{
                  components: {
                    Table: {
                      borderRadius: 0,
                      headerBorderRadius: 0
                    }
                  }
                }}
              >
                <Table
                  className="customTable"
                  columns={columns}
                  dataSource={dataTable}
                  rowKey={(record) =>
                    `${record.code ?? 'emp'}-${record.attendanceWorkDate ?? ''}`
                  }
                  loading={isLoading}
                  pagination={false}
                  onChange={handleTableChange}
                  sticky
                  scroll={{ x: 'max-content', y: '100%' }}
                />
              </ConfigProvider>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 16,
                margin: 24
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <span style={{ color: 'var(--color-on-base-content-low)' }}>
                  {t('hrms_attendance_row_per_page')}
                </span>
                <Select
                  size="small"
                  value={limit}
                  variant="borderless"
                  style={{ width: 80 }}
                  options={PAGE_SIZE_SELECT_OPTIONS}
                  onChange={(size) => handlePaginationChange(1, size)}
                />
              </div>
              <div>
                <Pagination
                  size="small"
                  current={page}
                  pageSize={limit}
                  total={
                    typeof totalRow === 'number' ? totalRow : dataTable.length
                  }
                  showSizeChanger={false}
                  style={{ whiteSpace: 'nowrap' }}
                  onChange={(p, ps) => handlePaginationChange(p, ps)}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </ConfigProvider>
  )
}
